/**
 * Validation for tour payment schedule templates.
 * Rules:
 * - Only single template OR array of templates with lead-time ranges may be set, not both.
 * - When multiple templates are selected, each must have lead_time_range and ranges must not overlap.
 */

export interface LeadTimeRange {
  min_days: number;
  max_days?: number;
}

export interface TemplateWithLeadTime {
  id: string;
  lead_time_range?: LeadTimeRange;
}

export interface TourPaymentScheduleFormData {
  paymentScheduleTemplateId?: string | null;
  paymentScheduleTemplateIds?: string[] | null;
  paymentScheduleDetails?: Array<TemplateWithLeadTime & { name?: string; description?: string }> | null;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Check if two lead-time ranges overlap.
 * Open-ended range (only min_days) is treated as [min_days, Infinity].
 */
function rangesOverlap(a: LeadTimeRange, b: LeadTimeRange): boolean {
  const minA = a.min_days;
  const maxA = a.max_days ?? Number.POSITIVE_INFINITY;
  const minB = b.min_days;
  const maxB = b.max_days ?? Number.POSITIVE_INFINITY;
  return !(maxA < minB || maxB < minA);
}

/**
 * Validate tour payment schedule: single template XOR array with non-overlapping date ranges.
 */
export function validateTourPaymentScheduleTemplates(
  paymentSchedule: TourPaymentScheduleFormData | null | undefined,
): ValidationResult {
  const errors: string[] = [];

  if (!paymentSchedule) {
    return { valid: true, errors: [] };
  }

  const singleId =
    paymentSchedule.paymentScheduleTemplateId != null && paymentSchedule.paymentScheduleTemplateId !== ''
      ? paymentSchedule.paymentScheduleTemplateId
      : null;
  const ids =
    Array.isArray(paymentSchedule.paymentScheduleTemplateIds) && paymentSchedule.paymentScheduleTemplateIds.length > 0
      ? paymentSchedule.paymentScheduleTemplateIds.filter(Boolean)
      : [];

  const hasSingle = singleId !== null;
  const hasArray = ids.length > 0;

  if (hasSingle && hasArray) {
    errors.push('Cannot have both a single payment schedule template and multiple templates. Use one or the other.');
    return { valid: false, errors };
  }

  if (ids.length <= 1) {
    return { valid: true, errors: [] };
  }

  const details = paymentSchedule.paymentScheduleDetails ?? [];
  const templates: TemplateWithLeadTime[] = ids
    .map((id) => details.find((d) => d.id === id))
    .filter((t): t is TemplateWithLeadTime & { lead_time_range: LeadTimeRange } => t != null);

  if (templates.length !== ids.length) {
    const missing = ids.filter((id) => !details.some((d) => d.id === id));
    errors.push(`Selected template(s) not found in schedule details: ${missing.join(', ')}`);
  }

  const missingRange = templates.filter((t) => !t.lead_time_range || t.lead_time_range.min_days == null);
  if (missingRange.length > 0) {
    errors.push(
      `Each template in a multi-template schedule must have a lead time range. Missing for: ${missingRange
        .map((t) => t.id)
        .join(', ')}`,
    );
  }

  const withRanges = templates.filter(
    (t): t is TemplateWithLeadTime & { lead_time_range: LeadTimeRange } => !!t.lead_time_range,
  );
  for (let i = 0; i < withRanges.length; i++) {
    for (let j = i + 1; j < withRanges.length; j++) {
      if (rangesOverlap(withRanges[i].lead_time_range!, withRanges[j].lead_time_range!)) {
        errors.push(
          `Lead time ranges overlap between "${withRanges[i].id}" and "${withRanges[j].id}". Ranges must not overlap.`,
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
